/**
 * AppliedCreditDebitNotesPanel - iter872
 * ──────────────────────────────────────
 * Inline panel for Invoice / Bill detail pages that lists every Credit Note
 * (sales side) or Debit Note (purchase side) applied to the document - in
 * the same visual style users expect for the Payments list. Each row offers:
 *
 *   • "Unapply"  - reverses the journal, restores balance_due, flags the
 *                  note as `open` so it can be re-applied later (non-
 *                  destructive). User explicitly chose this semantic in
 *                  iter872 plan confirmation.
 *
 * Props:
 *   document     - the invoice (kind='credit') or bill (kind='debit')
 *   kind         - 'credit' | 'debit'
 *   onChange     - callback after unapply succeeds (parent should refresh)
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Download, FileMinus, Loader2, Undo2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AppliedCreditDebitNotesPanel({ document: doc, kind, onChange }) {
  const isCredit = kind === 'credit';
  const labelNote = isCredit ? 'Credit Note' : 'Debit Note';
  const endpoint = isCredit ? 'credit-notes' : 'debit-notes';
  const docIdField = isCredit ? 'invoice_id' : 'bill_id';
  const numberField = isCredit ? 'credit_note_number' : 'debit_note_number';
  const dateField = isCredit ? 'credit_note_date' : 'debit_note_date';

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unapplying, setUnapplying] = useState(null);
  const docId = doc?.id;
  const currency = doc?.currency || 'NGN';

  const fetchNotes = useCallback(async () => {
    if (!docId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/${endpoint}?${docIdField}=${docId}`, { withCredentials: true });
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      // Only show notes still applied (status === 'issued').
      setNotes(list.filter(n => n.status === 'issued'));
    } catch (e) {
      // Silent - panel auto-hides on empty.
    } finally { setLoading(false); }
  }, [docId, endpoint, docIdField]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  async function unapply(note) {
    if (!window.confirm(
      `Unapply ${labelNote} ${note[numberField]}?\n\nThis reverses the journal and restores the ${isCredit ? 'invoice' : 'bill'} balance. The ${labelNote.toLowerCase()} stays in your records as Open so you can re-apply it to a different ${isCredit ? 'invoice' : 'bill'} later.`,
    )) return;
    setUnapplying(note.id);
    try {
      await axios.delete(`${API}/${endpoint}/${note.id}?mode=unapply`, { withCredentials: true });
      toast.success(`${labelNote} unapplied: balance restored`);
      await fetchNotes();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to unapply ${labelNote}`);
    } finally { setUnapplying(null); }
  }

  const fmt = (n) => `${currency} ${parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // iter1295 - download the note's own branded PDF
  const [downloadingId, setDownloadingId] = useState(null);
  async function downloadPdf(note) {
    setDownloadingId(note.id);
    try {
      const r = await axios.get(`${API}/${endpoint}/${note.id}/pdf`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `${note[numberField] || 'note'}.pdf`;
      window.document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Failed to download ${labelNote} PDF`);
    } finally {
      setDownloadingId(null);
    }
  }

  if (!loading && notes.length === 0) return null;

  return (
    <Card data-testid={`applied-${kind}-notes-panel`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <FileMinus className="w-4 h-4 text-orange-600" />
          Applied {labelNote}s
          {notes.length > 0 && (
            <Badge variant="outline" className="ml-1 text-xs">{notes.length}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground py-2 text-center">
            <Loader2 className="w-4 h-4 inline-block mr-2 animate-spin" />Loading…
          </div>
        ) : (
          <div className="space-y-2">
            {notes.map((n) => (
              <div key={n.id} className="px-3 py-2.5 border rounded-md space-y-1.5"
                data-testid={`applied-${kind}-note-${n.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-sm font-medium shrink-0">{n[numberField]}</span>
                  <div className="text-sm font-semibold whitespace-nowrap">{fmt(n.amount)}</div>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
                  <Badge variant="default" className="text-[10px] px-1.5 py-0 shrink-0">Issued</Badge>
                  <span className="truncate">
                    {n[dateField] || '-'} · {n.reason || 'No reason given'}
                    {parseFloat(n.tax_amount || 0) > 0 && <> · incl. tax {fmt(n.tax_amount)}</>}
                  </span>
                </div>
                <div className="flex items-center justify-end gap-1.5 pt-1">
                  <Button variant="outline" size="sm"
                    onClick={() => downloadPdf(n)} disabled={downloadingId === n.id}
                    data-testid={`download-${kind}-note-pdf-${n.id}`}
                    title={`Download ${labelNote} PDF`}
                    className="h-7 px-2 text-slate-600 hover:text-slate-900">
                    {downloadingId === n.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <><Download className="w-3 h-3 mr-1" />PDF</>
                    )}
                  </Button>
                  <Button variant="outline" size="sm"
                    onClick={() => unapply(n)} disabled={unapplying === n.id}
                    data-testid={`unapply-${kind}-note-${n.id}`}
                    className="h-7 px-2 text-orange-700 border-orange-300 hover:bg-orange-50">
                    {unapplying === n.id ? (
                      <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Unapplying…</>
                    ) : (
                      <><Undo2 className="w-3 h-3 mr-1" />Unapply</>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
